import { useState } from 'react';
import { Body1Strong, Button, Caption1, ProgressBar, makeStyles, tokens } from '@fluentui/react-components';
import { ChevronDownRegular, ChevronUpRegular } from '@fluentui/react-icons';

import { CHANGE_STATUS, LIFECYCLE_STEPS, TChangeV2 } from '../../models';
import { LifecycleStepper } from './LifecycleStepper';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  row: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalL },
  info: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '150px', flexShrink: 0 },
  muted: { color: tokens.colorNeutralForeground3 },
  bar: { flex: 1, minWidth: '120px' },
});

// A calm, single-line "where is this change" indicator. The full lifecycle is
// available on demand behind the expander rather than always shown.
export const CompactStageIndicator = ({ change }: { change: TChangeV2 }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  const total = LIFECYCLE_STEPS.length;
  const currentIndex = LIFECYCLE_STEPS.indexOf(change.lifecycleStep);
  const cancelled = change.status === CHANGE_STATUS.CANCELLED;
  const position = currentIndex >= 0 ? currentIndex + 1 : 1;
  const progress = cancelled ? 0 : position / total;

  return (
    <div className={classes.root}>
      <div className={classes.row}>
        <div className={classes.info}>
          <Body1Strong>{cancelled ? 'Cancelled' : change.lifecycleStep}</Body1Strong>
          <Caption1 className={classes.muted}>{cancelled ? change.status : `Stage ${position} of ${total}`}</Caption1>
        </div>
        <ProgressBar className={classes.bar} value={progress} thickness="large" color={cancelled ? 'error' : 'brand'} />
        <Button
          appearance="subtle"
          size="small"
          icon={expanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
        >
          {expanded ? 'Hide lifecycle' : 'View lifecycle'}
        </Button>
      </div>
      {expanded ? <LifecycleStepper currentStep={change.lifecycleStep} status={change.status} /> : null}
    </div>
  );
};
